/**
 * AES-256-GCM payload encryption helpers (Web Crypto).
 *
 * Wire format (base64, standard alphabet): iv[12] + authTag[16] + ciphertext.
 * Web Crypto expects ciphertext + tag, so the tag is moved on the way in/out.
 */

const IV_LENGTH = 12;
const TAG_LENGTH = 16;

const ENABLE_ENCRYPTION = import.meta.env.VITE_ENABLE_ENCRYPTION === 'true';
const KEY_HEX = import.meta.env.VITE_API_AES_KEY || '';

/** Requests sent as plain JSON, which the backend accepts. Their responses are still decrypted. */
const PLAIN_PREFIXES = ['/auth/login'];

// crypto.subtle only exists in secure contexts (HTTPS/localhost); fail open to plain JSON otherwise.
export const isEncryptionEnabled = () =>
  ENABLE_ENCRYPTION && /^[0-9a-fA-F]{64}$/.test(KEY_HEX) && !!globalThis.crypto?.subtle;

export const isPlainUrl = (url = '') => {
  const path = url.startsWith('/') ? url : `/${url}`;
  return PLAIN_PREFIXES.some((p) => path.startsWith(p));
};

const hexToBytes = (hex: string) =>
  Uint8Array.from(hex.match(/.{2}/g)!.map((b) => parseInt(b, 16)));

let keyPromise: Promise<CryptoKey> | null = null;
const getKey = () =>
  (keyPromise ??= crypto.subtle.importKey('raw', hexToBytes(KEY_HEX), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]));

const toB64 = (u8: Uint8Array) => {
  let s = '';
  u8.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s);
};

const fromB64 = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

/** object -> base64(iv + tag + ciphertext) */
export async function encryptPayload(data: unknown): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const plain = new TextEncoder().encode(JSON.stringify(data));
  const out = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, tagLength: TAG_LENGTH * 8 },
      await getKey(),
      plain
    )
  );
  const ct = out.slice(0, out.length - TAG_LENGTH);
  const tag = out.slice(out.length - TAG_LENGTH);
  const wire = new Uint8Array(IV_LENGTH + TAG_LENGTH + ct.length);
  wire.set(iv, 0);
  wire.set(tag, IV_LENGTH);
  wire.set(ct, IV_LENGTH + TAG_LENGTH);
  return toB64(wire);
}

/** base64(iv + tag + ciphertext) -> object */
export async function decryptPayload(b64: string): Promise<unknown> {
  const wire = fromB64(b64);
  const iv = wire.slice(0, IV_LENGTH);
  const tag = wire.slice(IV_LENGTH, IV_LENGTH + TAG_LENGTH);
  const ct = wire.slice(IV_LENGTH + TAG_LENGTH);
  const buf = new Uint8Array(ct.length + TAG_LENGTH);
  buf.set(ct, 0);
  buf.set(tag, ct.length);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: TAG_LENGTH * 8 },
    await getKey(),
    buf
  );
  return JSON.parse(new TextDecoder().decode(plain));
}

/** True when a response body is exactly `{ payload: "<string>" }`. */
export const isEncryptedBody = (body: unknown): body is { payload: string } =>
  !!body &&
  typeof body === 'object' &&
  typeof (body as { payload?: unknown }).payload === 'string' &&
  Object.keys(body as object).length === 1;
